/**
 * «+» доски расстановки (16.10.2026, владелец): «кроме квадратиков графика — грейды операторов на этом квесте и
 * количество отведённых игр за всю историю; если он уже поставлен на игру, которая пересекается с настраиваемой, —
 * квадратик в красной рамке, а по долгому нажатию — с чем конфликт».
 *  • грейды — GET /api/grades (все пары оператор×квест), игры на квесте — GET /api/grades/played-quest (то же правило
 *    игры, что «За всё время»). Каждое открытие «+» берёт свежие (грейд могли поменять в HR → «Грейды» минуту назад),
 *    а пока едут — показывает прошлые; сбой одного запроса не гасит другой (ревью 16.10);
 *  • пересечение — по ТЕКУЩЕЙ расстановке на доске (draft, вместе с несохранённым): оператор занят игрой от начала
 *    до конца уборки [время, время + длительность + уборка). Игры встык (одна кончилась с уборкой — другая началась)
 *    не пересекаются. Ночной хвост — по sortTime (часы +24). Спецверсия — по СВОЕЙ длительности и уборке
 *    (duration_min/reset_min версии, как гейт conflicts.ts eff()), иначе — квеста.
 */
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import type { BoardGame } from "../../hooks/useOoDay";
import { gradeKey, type Span } from "./opRules";
/* 20.10: чистые правила (грейд, пересечения, сортировка) — в opRules.ts: их тест идёт в официальном прогоне веба (node без tsx),
   а этот файл тянет api/client → api/types (параметр-свойства класса — node в режиме strip-only их не понимает). */
export * from "./opRules";

/* последние полученные — показать сразу при следующем открытии, пока едут свежие */
let lastGrades: Map<string, number> | null = null;
const lastPlayed = new Map<string, Map<string, number>>();
let spans: { at: number; map: Map<string, Span> } | null = null;

const fetchGrades = () => api<{ results?: { operator_id: string; quest_id: string; grade: number }[] }>("/api/grades")
  .then((r) => (lastGrades = new Map((r.results ?? []).map((g) => [gradeKey(g.operator_id, g.quest_id), g.grade]))));
const fetchPlayed = (quest: string) => api<{ results?: { operator_id: string; n: number }[] }>(`/api/grades/played-quest?quest_id=${encodeURIComponent(quest)}`)
  .then((r) => { const m = new Map((r.results ?? []).map((x) => [x.operator_id, x.n])); lastPlayed.set(quest, m); return m; });
/** Длительности спецверсий (вместе со снятыми — бронь на снятой стоит); меняются редко — раз в 10 минут. */
function fetchSpans(): Promise<Map<string, Span>> {
  if (spans && Date.now() - spans.at < 10 * 60_000) return Promise.resolve(spans.map);
  return api<{ results?: { id: string; duration_min: number | null; reset_min: number | null }[] }>("/api/quest-versions?archived=1")
    .then((r) => { const map = new Map((r.results ?? []).map((v) => [String(v.id), { duration: v.duration_min, reset: v.reset_min }])); spans = { at: Date.now(), map }; return map; });
}

export interface OpStats { grades: Map<string, number> | null; played: Map<string, number> | null; spans: Map<string, Span>; failed: boolean }
/** Грейды, игры на квесте и длительности версий для «+»; null — ещё грузится (и прошлых нет). */
export function useOpStats(quest: string): OpStats {
  const [s, setS] = useState<OpStats>(() => ({ grades: lastGrades, played: lastPlayed.get(quest) ?? null, spans: spans?.map ?? new Map(), failed: false }));
  useEffect(() => {
    let alive = true;
    const fail = () => { if (alive) setS((x) => ({ ...x, failed: true })); };
    fetchGrades().then((grades) => { if (alive) setS((x) => ({ ...x, grades })); }, fail);
    fetchPlayed(quest).then((played) => { if (alive) setS((x) => ({ ...x, played })); }, fail);
    fetchSpans().then((m) => { if (alive) setS((x) => ({ ...x, spans: m })); }, () => { /* без версий — длительность квеста, как раньше */ });
    return () => { alive = false; };
  }, [quest]);
  return s;
}
